/** Authorized child/team contexts for the current account. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { getGuardianLinksByParent } from '../lib/api';
import {
  contextKey, parentStorageKey, normalizeGuardianLinks, pickParentContext,
  childCount as countChildren, hasOwnTeamWorkspace, hasLeaderWorkspace, availableTeams,
} from '../lib/accountContexts';
import { getTeamRole } from '../lib/roles';

const ParentContext = createContext(undefined);

export function ParentProvider({ children }) {
  const auth = useAuth();
  const { user, teams, activeTeam, sessionVersion } = auth;
  const ownActiveTeam = hasOwnTeamWorkspace(user, activeTeam);
  const leaderWorkspace = hasLeaderWorkspace(user, activeTeam)
    || availableTeams(teams).some(team => hasLeaderWorkspace(user, team));
  const userId = user?.id || null;
  const scope = userId ? `${userId}:${sessionVersion}` : null;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const authRef = useRef(auth);
  authRef.current = auth;
  const requestId = useRef(0);
  const selection = useRef(null);
  const [data, setData] = useState({ scope: null, links: [], loading: false, error: null });
  const [saved, setSaved] = useState({ scope: null, key: null });
  // Never restore a player's child view from persisted preferences on login.
  const [chosen, setChosen] = useState(null);
  const [pending, setPending] = useState(null);

  // Mask stale data during render, before effects run on an account change.
  const links = useMemo(() => scope && data.scope === scope ? data.links : [], [scope, data]);
  const linksRef = useRef(links);
  linksRef.current = links;
  const loading = Boolean(scope) && (data.scope !== scope || data.loading);
  const error = data.scope === scope ? data.error : null;
  const switching = pending?.scope === scope && Boolean(scope);

  const load = useCallback(async () => {
    if (scopeRef.current !== scope) return false;
    const request = ++requestId.current;
    if (!userId || !scope) {
      selection.current = null;
      setPending(null);
      setSaved({ scope: null, key: null });
      setChosen(null);
      setData({ scope: null, links: [], loading: false, error: null });
      return false;
    }
    setData(previous => ({
      scope, links: previous.scope === scope ? previous.links : [], loading: true, error: null,
    }));
    try {
      // A coach can also be a guardian. The authorized API decides which links
      // belong to this account; no legacy/global role gate is used here.
      const response = await getGuardianLinksByParent(userId);
      if (scopeRef.current !== scope || requestId.current !== request) return false;
      const rows = normalizeGuardianLinks(response.data?.data);
      let key = null;
      try { key = localStorage.getItem(parentStorageKey(userId)); } catch { /* optional preference */ }
      setSaved({ scope, key });
      setData({ scope, links: rows, loading: false, error: null });
      return true;
    } catch (_error) {
      if (scopeRef.current !== scope || requestId.current !== request) return false;
      setData({ scope, links: [], loading: false, error: 'Barnens lag kunde inte hämtas. Försök igen.' });
      return false;
    }
  }, [userId, scope]);

  useEffect(() => {
    // Do not adopt the old preference, which was shared by every account.
    try { localStorage.removeItem('trainova:active_parent_context'); } catch { /* optional */ }
    load();
    const onFocus = () => { if (document.visibilityState !== 'hidden') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      requestId.current += 1;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const savedKey = saved.scope === scope ? saved.key : null;
  const explicit = chosen?.scope === scope && chosen.teamId === activeTeam?.id ? chosen : null;
  // Keep the chosen mode while revalidating a link, even if it is removed or
  // the request fails. A missing child must not silently become another person.
  const isParentView = !leaderWorkspace && Boolean(explicit || switching || (!ownActiveTeam && (
    getTeamRole(user, activeTeam?.id) === 'parent' || links.some(link => link.team_id === activeTeam?.id)
  )));
  const contextUnavailable = loading || switching || auth.teamSwitching || Boolean(auth.teamSwitchError);
  const active = useMemo(() => contextUnavailable || !isParentView ? null : pickParentContext(
    links,
    savedKey,
    activeTeam?.id,
    { preferOwnTeam: ownActiveTeam, explicitKey: explicit?.key, disabled: leaderWorkspace },
  ), [links, savedKey, activeTeam?.id, ownActiveTeam, explicit?.key, leaderWorkspace, isParentView, contextUnavailable]);

  const clearActiveContext = useCallback(() => {
    if (!scope || scopeRef.current !== scope || selection.current?.scope === scope) return;
    setSaved({ scope, key: null });
    setChosen(null);
    setPending(null);
    try { localStorage.removeItem(parentStorageKey(userId)); } catch { /* optional */ }
  }, [scope, userId]);

  const setActiveContext = useCallback(async ({ playerProfileId, teamId }) => {
    if (!scope || scopeRef.current !== scope) throw new Error('Kontot har ändrats. Välj barn igen.');
    if (loading) throw new Error('Barnlistan uppdateras. Vänta tills den är klar.');
    const currentAuth = authRef.current;
    if (hasLeaderWorkspace(currentAuth.user, currentAuth.activeTeam)
        || availableTeams(currentAuth.teams).some(team => hasLeaderWorkspace(currentAuth.user, team))) {
      throw new Error('Ditt konto använder lagets ledarläge.');
    }
    const key = contextKey(playerProfileId, teamId);
    const link = linksRef.current.find(item => contextKey(item.player_profile_id, item.team_id) === key);
    if (!availableTeams(authRef.current.teams).some(team => team.id === teamId)) {
      throw new Error('Barnets lag är inte tillgängligt. Uppdatera laglistan.');
    }
    if (!key || !link) throw new Error('Barnet är inte kopplat till ditt konto i det här laget.');
    if (selection.current?.scope === scope) throw new Error('Ett byte pågår. Vänta tills det är klart.');
    const operation = { scope, key, teamId };
    selection.current = operation;
    setPending(operation);
    try {
      if (authRef.current.activeTeam?.id !== teamId || authRef.current.teamSwitchError) {
        const confirmed = await authRef.current.changeActiveTeam(teamId);
        if (confirmed?.id !== teamId) throw new Error('Lagbytet kunde inte bekräftas. Välj barn igen.');
      } else if (authRef.current.teamSwitching) {
        throw new Error('Ett lagbyte pågår. Vänta tills det är klart.');
      }
      if (scopeRef.current !== scope) throw new Error('Kontot har ändrats. Välj barn igen.');
      if (!linksRef.current.some(item => contextKey(item.player_profile_id, item.team_id) === key)) {
        throw new Error('Barnkopplingen har ändrats. Välj barn igen.');
      }
      setSaved({ scope, key });
      setChosen(operation);
      try { localStorage.setItem(parentStorageKey(userId), key); } catch { /* optional preference */ }
      return link;
    } finally {
      if (selection.current === operation) {
        selection.current = null;
        if (scopeRef.current === scope) setPending(null);
      }
    }
  }, [scope, links, userId, loading]);

  const setActiveChildId = useCallback((id) => {
    const link = links.find(item => item.player_profile_id === id && item.team_id === activeTeam?.id)
      || links.find(item => item.player_profile_id === id);
    if (!link) return Promise.resolve(null);
    return setActiveContext({ playerProfileId: link.player_profile_id, teamId: link.team_id });
  }, [links, activeTeam?.id, setActiveContext]);

  const value = useMemo(() => ({
    isParent: links.length > 0,
    isParentView,
    leaderWorkspace,
    loading, error, switching,
    contexts: links,
    children: links,
    childCount: countChildren(links),
    contextCount: links.length,
    teamCount: new Set(links.map(link => link.team_id)).size,
    active,
    activeChildId: active?.player_profile_id || null,
    activeTeamId: active?.team_id || null,
    activePlayerUserId: active?.player_user_id || null,
    activeContextKey: active ? contextKey(active.player_profile_id, active.team_id) : null,
    setActiveContext, setActiveChildId, clearActiveContext, refresh: load,
  }), [links, loading, error, switching, active, isParentView, leaderWorkspace, setActiveContext, setActiveChildId, clearActiveContext, load]);

  return <ParentContext.Provider value={value}>{children}</ParentContext.Provider>;
}

export function useParent() {
  const ctx = useContext(ParentContext);
  if (!ctx) throw new Error('useParent must be used inside <ParentProvider>');
  return ctx;
}
